import i18n from 'i18next';
import { parse, parseISO, isBefore, isEqual } from 'date-fns';
import { AviaPassengerType, AviaTraveller, TravellerParameter, TravellerFieldEnum, DocTypes } from '@websky/graphql';
import { getTravellerFullName, getPassengerTypeLabel } from '../utils';
import { formatInvalidPassengerDate, hideText } from '../Passenger/utils';

export const collectPassengerInfo = (traveller: AviaTraveller) => {
	let travellerName: string;
	const travellerMainInfo: string[] = [];
	const travellerAdditionalInfo: string[] = [];
	const travellerLoyaltyInfo: string[] = [];

	const travellerValues = new Map<TravellerFieldEnum, TravellerParameter>(
		traveller.values.map(params => [params.type, params])
	);

	if (travellerValues.has(TravellerFieldEnum.LastName)) {
		travellerName = getTravellerFullName(traveller);
	} else {
		travellerName = getPassengerTypeLabel(traveller.type as AviaPassengerType, traveller.supplierTravellerType);
	}

	if (travellerValues.has(TravellerFieldEnum.Gender)) {
		const genderValue = travellerValues.get(TravellerFieldEnum.Gender).value;
		const genderName = travellerValues
			.get(TravellerFieldEnum.Gender)
			?.validationRules?.[0].options.find(({ value }) => value === genderValue)?.label;

		if (genderName) {
			travellerMainInfo.push(i18n.t(`OrderCard:Gender_${genderName}`));
		}
	}

	if (travellerValues.has(TravellerFieldEnum.BirthDate)) {
		const birthDate = formatInvalidPassengerDate(
			TravellerFieldEnum.BirthDate,
			travellerValues.get(TravellerFieldEnum.BirthDate).value
		);

		travellerMainInfo.push(birthDate);
	}

	const nationalityValue = travellerValues.has(TravellerFieldEnum.Nationality)
		? travellerValues.get(TravellerFieldEnum.Nationality).value
		: null;
	if (nationalityValue) {
		const nationalityName = travellerValues
			.get(TravellerFieldEnum.Nationality)
			?.validationRules?.[0].options.find(({ value }) => value === nationalityValue)?.label;

		if (nationalityName) {
			travellerMainInfo.push(nationalityName);
		}
	}
	if (travellerValues.has(TravellerFieldEnum.DocType)) {
		const docType = travellerValues.get(TravellerFieldEnum.DocType).value;
		const docNumber = travellerValues.get(TravellerFieldEnum.DocNumber)?.value;

		const isPassport = docType === DocTypes.Passport;
		const isBirthRegDocument = docType === DocTypes.BirthRegDocument;
		const isHideDocExpiryDate = isPassport || isBirthRegDocument;

		if (docNumber) {
			travellerAdditionalInfo.push(`${i18n.t(`Passenger:${docType}`)}: ${hideText(docNumber)}`);
		}

		if (!isHideDocExpiryDate && travellerValues.get(TravellerFieldEnum.DocExpiryDate)?.value) {
			const expireDate = formatInvalidPassengerDate(
				TravellerFieldEnum.DocExpiryDate,
				travellerValues.get(TravellerFieldEnum.DocExpiryDate).value
			);
			travellerAdditionalInfo.push(`${i18n.t('OrderCard:Expired date')}: ${expireDate}`);
		}
	}

	if (
		travellerValues.has(TravellerFieldEnum.LoyaltyNumber) &&
		travellerValues.get(TravellerFieldEnum.LoyaltyNumber)?.value
	) {
		const loyaltyNumber = travellerValues.get(TravellerFieldEnum.LoyaltyNumber).value;
		travellerLoyaltyInfo.push(`${i18n.t('Passenger:loyaltyName')}: ${loyaltyNumber}`);
	}

	return {
		travellerName,
		mainInfo: travellerMainInfo,
		additionalInfo: travellerAdditionalInfo,
		loyaltyInfo: travellerLoyaltyInfo,
		ticketNumbers: traveller.tickets?.map(ticket => ticket?.number) || []
	};
};

// ключ - кто сопровождает, значение - кого сопровождают
export const getAdultAccompaniedMap = (travellers: AviaTraveller[]) => {
	const passengerAccompaniedMap = new Map<string, AviaTraveller[]>();

	travellers.forEach(traveller => {
		if (traveller.linkedTraveller?.id) {
			passengerAccompaniedMap.set(traveller.linkedTraveller.id, [
				...(passengerAccompaniedMap.get(traveller.linkedTraveller.id) ?? []),
				traveller
			]);
		}
	});

	return passengerAccompaniedMap;
};

export const isTravellerOlderThanMinDate = (traveller: AviaTraveller, relinkingTraveller: AviaTraveller) => {
	const minBirthDateAsStr = relinkingTraveller.values.find(value => value.type === TravellerFieldEnum.LinkedTraveller)
		?.validationRules?.[0]?.minDate;

	const travellerBirthDateAsStr = traveller.values.find(value => value.type === TravellerFieldEnum.BirthDate)?.value;

	if (!minBirthDateAsStr || !travellerBirthDateAsStr) {
		return true;
	}

	const travellerBirthDate = parse(travellerBirthDateAsStr, 'yyyy-MM-dd', new Date());
	const minBirthDate = parseISO(minBirthDateAsStr);

	return isBefore(travellerBirthDate, minBirthDate) || isEqual(travellerBirthDate, minBirthDate);
};
